import { useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../../../app/pwa/customerFormDirtyGate.js';
import { isPlatformChromeTenant } from '../../../../app/platformChromeTenant.js';
import { toCustomerFacingErrorMessage } from '../../../../shared/errors/toCustomerFacingErrorMessage.js';
import { Card } from '../../../../shared/ui/Card.js';
import { Button } from '../../../../shared/ui/Button.js';
import { FormField } from '../../../../shared/ui/FormField.js';
import { showToast } from '../../../../shared/ui/toastStore.js';
import { hasCustomerApiTenant } from '../../../../shared/tenant/customerApiTenant.js';
import { isReservedCustomerPathSegment } from '../../../auth/customerAuthPaths.js';
import { CheckoutGuestSignInPrompt } from '../../../checkout/CheckoutGuestSignInPrompt.js';
import { CheckoutModeSummaryCard } from '../../../checkout/components/CheckoutModeSummaryCard.js';
import { GatewayMethodsStep } from '../../../checkout/components/GatewayMethodsStep.js';
import { PaymentMethodPicker } from '../../../checkout/components/PaymentMethodPicker.js';
import { PayObligationCtaShell } from '../../../checkout/components/PayObligationCtaShell.js';
import { PrecontractInfoShell } from '../../../checkout/components/PrecontractInfoShell.js';
import { CheckoutCurrencyPicker } from '../../../checkout/components/CheckoutCurrencyPicker.js';
import { CheckoutPromoPreview } from '../../../checkout/CheckoutPromoPreview.js';
import { CheckoutLoyaltyPreview } from '../../../checkout/CheckoutLoyaltyPreview.js';
import { resolveCheckoutModeDetailI18nKey } from '../../../checkout/checkoutModePresentation.js';
import { buildPrecontractShellSlots } from '../../../checkout/presenters/buildPrecontractShellSlots.js';
import { CheckoutTypedPromoCodeBlock } from '../../../promotions/CheckoutTypedPromoCodeBlock.js';
import { PromoExclusiveConflictModal } from '../../../promotions/PromoExclusiveConflictModal.js';
import { isCustomerCheckoutCartLocked } from '../../../promotions/checkoutCartLock.js';
import { resolveGatewayProviderSlotId } from '../../../checkout/resolveGatewayProviderSlotId.js';
import { CheckoutGatewayRedirectLink } from '../../phoneFirstProductCheckoutUtils.js';
import type { UsePhoneFirstCheckoutScreenResult } from '../../hooks/usePhoneFirstCheckoutScreen.js';
import { resolveEmptyPaymentMethodsMessageKey } from './resolveEmptyPaymentMethodsMessageKey.js';
import { LowStockReservationCountdown } from '../../../shop/components/LowStockReservationCountdown.js';
import { MixedCartRefundTermsNotice } from '../../../checkout/components/MixedCartRefundTermsNotice.js';
import { SellerLegalIdentityShell } from '../../../seller-legal/SellerLegalIdentityShell.js';
import { usePublicSellerLegalQuery } from '../../../seller-legal/publicSellerLegalApi.js';
import { AgeGateDialog } from '../../../mor-chrome/AgeGateDialog.js';
import {
  certifyAgeGate,
  isAgeGateDependencyResolved,
} from '../../../mor-chrome/ageGateModel.js';
import { PaymentMarksStrip } from '../../../mor-chrome/PaymentMarksStrip.js';
import { ShippingDisclosureBanner } from '../../../mor-chrome/ShippingDisclosureBanner.js';
import { resolveCheckoutSalesPointIdFromSession } from '../../../checkout/checkoutSalesPointId.js';
import { useShopProducts } from '../../../shop/shopApi.js';
import { SellerAssistedPhoneReview } from '../../../seller-assisted/SellerAssistedPhoneReview.js';
import type { CheckoutSessionDisplayLine } from 'pi-kiosk-shared';

export type PhoneFirstCheckoutPaymentStepViewProps = Pick<
  UsePhoneFirstCheckoutScreenResult,
  | 't'
  | 'sessionError'
  | 'sessionLoading'
  | 'checkoutSession'
  | 'selectedCheckoutMode'
  | 'selfCollectConfirmAtKiosk'
  | 'sessionCollect'
  | 'isDonationSession'
  | 'pricingPreview'
  | 'promotionsEnabled'
  | 'loyaltyEnabled'
  | 'promotionsProgramEnabled'
  | 'showTypedPromoBlock'
  | 'showPromoPreview'
  | 'showLoyaltyPreview'
  | 'incentiveProgramsEnabled'
  | 'promoEventNameById'
  | 'promoExclusiveConflict'
  | 'awaitingPaymentOnServer'
  | 'activeQrPayment'
  | 'pendingQr'
  | 'resumedQrFromSession'
  | 'handleResumeAwaitingPayment'
  | 'authSession'
  | 'email'
  | 'setEmail'
  | 'userEditedEmailRef'
  | 'emailError'
  | 'showSlaNotice'
  | 'setSlaNoticeDismissed'
  | 'mixedCartRefundTermLines'
  | 'handleLowStockReservationExpired'
  | 'holdExpiryReverify'
  | 'paymentRailUiStep'
  | 'gatewayMethodOptions'
  | 'gatewayMethodsResponse'
  | 'gatewayMethodsLoading'
  | 'gatewayStartingMethodId'
  | 'payActionsDisabled'
  | 'handleGatewayMethodSelect'
  | 'gateway'
  | 'paymentError'
  | 'visiblePaymentMethods'
  | 'cashSignInRequired'
  | 'mobileQrAllowed'
  | 'qrRailCandidate'
  | 'gatewayMethodsProbePending'
  | 'gatewayMethodsPayable'
  | 'gatewayRailCandidate'
  | 'skipPaymentMethodPicker'
  | 'paymentPickerOptions'
  | 'selectedPaymentMethod'
  | 'setSelectedPaymentMethodOverride'
  | 'subtitleAmountMinor'
  | 'subtitleCurrency'
  | 'showCheckoutCurrencyPicker'
  | 'checkoutCurrencyOptions'
  | 'selectedCheckoutCurrency'
  | 'currencyLocked'
  | 'currencySwitchPending'
  | 'currencySwitchError'
  | 'handleCheckoutCurrencySelect'
  | 'resolvePaymentConfirmLabel'
  | 'handlePaymentConfirm'
  | 'paymentConfirmPending'
  | 'sessionIdFromUrl'
  | 'tenantCode'
  | 'locationPathAndSearch'
  | 'navigate'
  | 'sellerAssistedCheckout'
  | 'sellerAssistedDisplayLines'
  | 'sellerAssistedStaleHandoff'
  | 'sellerAssistedMerchantName'
  | 'sellerAssistedSalesPointName'
>;

export function PhoneFirstCheckoutPaymentStepView(
  props: PhoneFirstCheckoutPaymentStepViewProps,
): JSX.Element {
  const {
    t,
    sessionError,
    sessionLoading,
    checkoutSession,
    selectedCheckoutMode,
    selfCollectConfirmAtKiosk,
    sessionCollect,
    isDonationSession,
    pricingPreview,
    promotionsProgramEnabled,
    showTypedPromoBlock,
    showPromoPreview,
    showLoyaltyPreview,
    incentiveProgramsEnabled,
    promoEventNameById,
    promoExclusiveConflict,
    awaitingPaymentOnServer,
    activeQrPayment,
    pendingQr,
    resumedQrFromSession,
    handleResumeAwaitingPayment,
    authSession,
    email,
    setEmail,
    userEditedEmailRef,
    emailError,
    showSlaNotice,
    setSlaNoticeDismissed,
    mixedCartRefundTermLines,
    handleLowStockReservationExpired,
    holdExpiryReverify,
    paymentRailUiStep,
    gatewayMethodOptions,
    gatewayMethodsResponse,
    gatewayMethodsLoading,
    gatewayStartingMethodId,
    payActionsDisabled,
    handleGatewayMethodSelect,
    gateway,
    paymentError,
    visiblePaymentMethods,
    cashSignInRequired,
    mobileQrAllowed,
    qrRailCandidate,
    gatewayMethodsProbePending,
    gatewayMethodsPayable,
    gatewayRailCandidate,
    skipPaymentMethodPicker,
    paymentPickerOptions,
    selectedPaymentMethod,
    setSelectedPaymentMethodOverride,
    subtitleAmountMinor,
    subtitleCurrency,
    showCheckoutCurrencyPicker,
    checkoutCurrencyOptions,
    selectedCheckoutCurrency,
    currencyLocked,
    currencySwitchPending,
    currencySwitchError,
    handleCheckoutCurrencySelect,
    resolvePaymentConfirmLabel,
    handlePaymentConfirm,
    paymentConfirmPending,
    sessionIdFromUrl,
    tenantCode,
    locationPathAndSearch,
    navigate,
    sellerAssistedCheckout,
    sellerAssistedDisplayLines,
    sellerAssistedStaleHandoff,
    sellerAssistedMerchantName,
    sellerAssistedSalesPointName,
  } = props;

  const [payObligationConfirmed, setPayObligationConfirmed] = useState(false);
  /** B01 DTO when APPROVED; precontract seller slot uses this or placeholder. */
  const sellerLegalQuery = usePublicSellerLegalQuery(tenantCode);
  const salesPointId = resolveCheckoutSalesPointIdFromSession(checkoutSession);
  const shopProductsQuery = useShopProducts(salesPointId);
  const catalogAgeGateEnabled = salesPointId !== null && salesPointId > 0;
  const sellerLegalAgeGateEnabled =
    !isPlatformChromeTenant(tenantCode) &&
    !isReservedCustomerPathSegment(tenantCode) &&
    hasCustomerApiTenant(tenantCode);
  // G2 — isAgeGateDependencyResolved is fail-closed on isError (isSuccess only).
  const ageGateInputsResolved =
    isDonationSession ||
    (isAgeGateDependencyResolved(shopProductsQuery, catalogAgeGateEnabled) &&
      isAgeGateDependencyResolved(sellerLegalQuery, sellerLegalAgeGateEnabled));
  const ageGateCert = useMemo(
    () =>
      isDonationSession
        ? ({ ok: true, required: false } as const)
        : certifyAgeGate({
            tenantCode,
            products: shopProductsQuery.data ?? [],
            catalogResolved: isAgeGateDependencyResolved(
              shopProductsQuery,
              catalogAgeGateEnabled,
            ),
            reviewRisk: sellerLegalQuery.data?.reviewRisk,
            sellerLegalResolved: isAgeGateDependencyResolved(
              sellerLegalQuery,
              sellerLegalAgeGateEnabled,
            ),
          }),
    [
      catalogAgeGateEnabled,
      isDonationSession,
      sellerLegalAgeGateEnabled,
      sellerLegalQuery,
      shopProductsQuery,
      tenantCode,
    ],
  );
  const ageGateBlocksPay = !ageGateCert.ok;

  const notifyAgeGateBlocked = useCallback((): void => {
    if (ageGateCert.ok) {
      return;
    }
    showToast({
      message: t(
        ageGateCert.reason === 'unresolved'
          ? 'morChrome.ageGate.pending'
          : 'morChrome.ageGate.requiredToast',
      ),
      variant: 'info',
    });
  }, [ageGateCert, t]);

  const confirmPaymentIfAgeGateAllows = useCallback((): void => {
    if (ageGateBlocksPay) {
      notifyAgeGateBlocked();
      return;
    }
    handlePaymentConfirm();
  }, [ageGateBlocksPay, handlePaymentConfirm, notifyAgeGateBlocked]);

  const selectGatewayMethodIfAgeGateAllows = useCallback(
    (methodId: string): void => {
      if (ageGateBlocksPay) {
        notifyAgeGateBlocked();
        return;
      }
      handleGatewayMethodSelect(methodId);
    },
    [ageGateBlocksPay, handleGatewayMethodSelect, notifyAgeGateBlocked],
  );

  const paymentHoldActive = isCustomerCheckoutCartLocked({
    awaitingPaymentOnServer,
    hasActiveQrPayment: activeQrPayment !== null,
    hasActiveGatewayPayment: gateway !== null,
  });

  const precontractSlots =
    checkoutSession !== null
      ? buildPrecontractShellSlots({
          displayLines: checkoutSession.displayLines ?? sellerAssistedDisplayLines,
          lines: checkoutSession.lines,
          sellerLegal: sellerLegalQuery.data ?? null,
          amountMinor: subtitleAmountMinor,
          currency: subtitleCurrency,
          fulfillmentTermsLabel:
            selectedCheckoutMode !== null
              ? t(
                  resolveCheckoutModeDetailI18nKey(selectedCheckoutMode, 'title', {
                    atKiosk: selfCollectConfirmAtKiosk,
                    collectTiming: sessionCollect?.timing ?? null,
                  }),
                )
              : null,
        })
      : null;

  const gatewayHint = ((): JSX.Element | null => {
    if (gateway === null) {
      return null;
    }
    if (/^https?:\/\//i.test(gateway.redirectUrl)) {
      return (
        <Trans
          i18nKey="checkout.gatewayRedirectHint"
          ns="customer"
          components={{
            1: <CheckoutGatewayRedirectLink to={gateway.redirectUrl} />,
          }}
        />
      );
    }
    return <>{t('checkout.gatewayNoRedirect')}</>;
  })();

  const renderPrimaryPaymentMethodSection = (): JSX.Element => {
    // Only block the whole rail while probing when nothing else is offerable yet.
    if (gatewayMethodsProbePending && visiblePaymentMethods.length === 0) {
      return (
        <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
          {t('payment.gatewayMethods.loading')}
        </p>
      );
    }
    if (visiblePaymentMethods.length === 0) {
      const emptyMessage = t(
        resolveEmptyPaymentMethodsMessageKey({
          gatewayMethodsPayable,
          mobileQrAllowed,
          gatewayRailCandidate,
          qrRailCandidate,
        }),
      );
      return (
        <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
          {emptyMessage}
        </p>
      );
    }
    const payCtaBlocked =
      payActionsDisabled || !payObligationConfirmed || ageGateBlocksPay;

    if (skipPaymentMethodPicker) {
      return (
        <div
          className="flex flex-col gap-3 border-t border-[var(--color-border)] pt-4"
          data-testid="customer-single-payment-method"
        >
          {checkoutSession?.fulfillmentMode === 'SHIPPING' ? (
            <div
              className="flex flex-col gap-1 rounded-lg border border-[var(--color-border)] p-3 text-sm"
              data-testid="shipping-checkout-summary"
            >
              <span className="font-semibold">{t('checkout.shipping.summaryTitle')}</span>
              <span>{t('checkout.shipping.summaryOutOfBandFee')}</span>
              <span className="text-[var(--color-on-surface-muted)]">
                {t('checkout.shipping.lateReportAdvancePromise')}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <span className="text-base font-medium text-[var(--color-on-surface)]">
              {t('payment.totalDue')}
            </span>
            <span className="text-xl font-bold text-[var(--color-on-surface)]">
              {(subtitleAmountMinor / 100).toFixed(2)} {subtitleCurrency}
            </span>
          </div>
          <Button
            intent="primary"
            size="lg"
            block
            type="button"
            data-testid="customer-payment-pay-cta"
            disabled={payCtaBlocked || paymentConfirmPending}
            onClick={confirmPaymentIfAgeGateAllows}
          >
            {resolvePaymentConfirmLabel()}
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {checkoutSession?.fulfillmentMode === 'SHIPPING' ? (
          <div
            className="flex flex-col gap-1 rounded-lg border border-[var(--color-border)] p-3 text-sm"
            data-testid="shipping-checkout-summary"
          >
            <span className="font-semibold">{t('checkout.shipping.summaryTitle')}</span>
            <span>{t('checkout.shipping.summaryOutOfBandFee')}</span>
            <span className="text-[var(--color-on-surface-muted)]">
              {t('checkout.shipping.lateReportAdvancePromise')}
            </span>
            {checkoutSession.shipping?.etaMinBusinessDays != null &&
            checkoutSession.shipping?.etaMaxBusinessDays != null ? (
              <span data-testid="shipping-payment-eta">
                {t('checkout.shipping.etaRange', {
                  min: checkoutSession.shipping.etaMinBusinessDays,
                  max: checkoutSession.shipping.etaMaxBusinessDays,
                })}
              </span>
            ) : null}
          </div>
        ) : null}
        <PaymentMethodPicker
          options={paymentPickerOptions}
          selectedId={selectedPaymentMethod}
          onSelect={setSelectedPaymentMethodOverride}
          amountMinor={subtitleAmountMinor}
          currency={subtitleCurrency}
          ctaLabel={resolvePaymentConfirmLabel()}
          ctaDisabled={payCtaBlocked}
          ctaPending={paymentConfirmPending}
          onConfirm={confirmPaymentIfAgeGateAllows}
        />
      </div>
    );
  };

  return (
    <Card padded>
      <div className="flex flex-col gap-4">
        {sessionError !== null ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {toCustomerFacingErrorMessage(sessionError, {
              fallback: t('checkout.sessionLoadError'),
            })}
          </p>
        ) : null}
        {sessionLoading ? (
          <p className="text-sm text-[var(--color-on-surface-muted)]">{t('checkout.loadingOrder')}</p>
        ) : null}
        {checkoutSession?.lowStockReservationExpiresAt != null &&
        checkoutSession.lowStockReservationExpiresAt.length > 0 ? (
          <LowStockReservationCountdown
            expiresAt={checkoutSession.lowStockReservationExpiresAt}
            onExpired={handleLowStockReservationExpired}
          />
        ) : null}
        {holdExpiryReverify?.expired === true && holdExpiryReverify.hasBlockingIssues ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert" data-testid="low-stock-hold-reverify-blocked">
            {t('shop.lowStockHold.reVerifyBlocked', {
              defaultValue:
                'Your hold expired. Some items are no longer available or prices changed — update your cart before paying.',
            })}
          </p>
        ) : null}
        {sellerAssistedCheckout && checkoutSession !== null ? (
          <SellerAssistedPhoneReview
            merchantName={sellerAssistedMerchantName}
            salesPointName={sellerAssistedSalesPointName}
            lines={sellerAssistedDisplayLines as readonly CheckoutSessionDisplayLine[]}
            amountMinor={checkoutSession.amountMinor}
            currency={checkoutSession.currency}
            staleHandoff={sellerAssistedStaleHandoff}
          />
        ) : null}
        {checkoutSession !== null && sessionCollect !== null && selectedCheckoutMode !== null ? (
          <CheckoutModeSummaryCard
            mode={selectedCheckoutMode}
            selfCollectConfirmAtKiosk={selfCollectConfirmAtKiosk}
            collectTiming={sessionCollect?.timing ?? null}
          />
        ) : null}
        {!isDonationSession && checkoutSession !== null && !sellerAssistedCheckout ? (
          <>
            {showTypedPromoBlock ? (
              <CheckoutTypedPromoCodeBlock
                checkoutSessionPublicId={sessionIdFromUrl}
                previewNetworkFailed={pricingPreview.isError}
                tenantCode={tenantCode}
                locationPathAndSearch={locationPathAndSearch}
                navigate={navigate}
                currency={subtitleCurrency}
                paymentHoldActive={paymentHoldActive}
                promoClaimReleased={pricingPreview.data?.promoClaimReleased === true}
                promotionsProgramEnabled={promotionsProgramEnabled}
              />
            ) : null}
            {showPromoPreview ? (
              <CheckoutPromoPreview
                preview={pricingPreview.data}
                isLoading={pricingPreview.isLoading}
                isError={pricingPreview.isError}
                baseAmountMinor={subtitleAmountMinor}
                currency={subtitleCurrency}
                eventNameById={promoEventNameById}
              />
            ) : null}
            {showLoyaltyPreview ? (
              <CheckoutLoyaltyPreview
                loyalty={pricingPreview.data?.loyalty ?? null}
                isLoading={pricingPreview.isLoading}
                currency={subtitleCurrency}
              />
            ) : null}
          </>
        ) : null}
        {incentiveProgramsEnabled && !isDonationSession && checkoutSession !== null ? (
          <PromoExclusiveConflictModal
            isOpen={promoExclusiveConflict.showExclusiveModal}
            onChoosePromo={promoExclusiveConflict.choosePromo}
            onChooseLoyalty={promoExclusiveConflict.chooseLoyalty}
            onClose={promoExclusiveConflict.closeExclusiveModal}
            isChoosingLoyalty={promoExclusiveConflict.isChoosingLoyalty}
            chooseLoyaltyErrorKey={promoExclusiveConflict.chooseLoyaltyErrorKey}
          />
        ) : null}
        {showCheckoutCurrencyPicker && selectedCheckoutCurrency !== null ? (
          <CheckoutCurrencyPicker
            currencies={checkoutCurrencyOptions}
            selected={selectedCheckoutCurrency}
            locked={currencyLocked}
            pending={currencySwitchPending}
            onSelect={(code) => {
              void handleCheckoutCurrencySelect(code);
            }}
          />
        ) : null}
        {currencySwitchError !== null ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {currencySwitchError}
          </p>
        ) : null}
        {awaitingPaymentOnServer &&
        activeQrPayment === null &&
        (pendingQr !== null || resumedQrFromSession !== null) ? (
          <div className="rounded-lg border border-amber-500/35 bg-amber-500/10 px-3 py-3">
            <p className="text-sm text-[var(--color-on-surface)]">
              {t('checkout.modeLockedPaymentInProgress')}
            </p>
            <Button
              intent="secondary"
              size="sm"
              className="mt-3"
              type="button"
              disabled={payActionsDisabled}
              onClick={handleResumeAwaitingPayment}
            >
              {paymentConfirmPending
                ? t('checkout.preparingQr')
                : t('checkout.resumeAwaitingPayment')}
            </Button>
          </div>
        ) : null}
        {authSession === null ? (
          <div
            {...{
              [CUSTOMER_FORM_DIRTY_ATTR]: email.trim().length > 0 ? 'true' : 'false',
            }}
          >
            <FormField
              label={t('checkout.emailLabel')}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('checkout.emailPlaceholder')}
              value={email}
              onChange={(e) => {
                userEditedEmailRef.current = true;
                setEmail(e.target.value);
              }}
              // G12 (b) — single-field guest email: errorText-alone (no FormErrorSummary).
              errorText={authSession === null ? (emailError ?? undefined) : undefined}
              helperText={t('checkout.emailHelper')}
            />
            <CheckoutGuestSignInPrompt tenantCode={tenantCode} source="checkout" />
            {cashSignInRequired ? (
              <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
                {t('checkout.cashSignInRequired')}
              </p>
            ) : null}
          </div>
        ) : null}
        {showSlaNotice ? (
          <div
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
            role="status"
          >
            <p className="text-sm text-[var(--color-on-surface)]">{t('checkout.slaNotice.banner')}</p>
            <button
              type="button"
              className="mt-2 text-xs text-[var(--color-accent)] underline"
              onClick={() => {
                setSlaNoticeDismissed(true);
              }}
            >
              {t('checkout.slaNotice.dismiss')}
            </button>
          </div>
        ) : null}
        {!isDonationSession ? (
          <MixedCartRefundTermsNotice lines={mixedCartRefundTermLines} legalFloors={null} />
        ) : null}
        {/* B01 / H09-TECH — checkout surface seller identity (technical fields only). */}
        {!isDonationSession ? (
          <SellerLegalIdentityShell
            apiTenantCode={tenantCode}
            testIdPrefix="customer-seller-legal-checkout"
          />
        ) : null}
        {!isDonationSession ? (
          <>
            <PaymentMarksStrip testIdPrefix="customer-payment-marks-checkout" compact />
            <ShippingDisclosureBanner
              salesPointId={resolveCheckoutSalesPointIdFromSession(checkoutSession)}
              testIdPrefix="customer-shipping-disclosure-checkout"
            />
          </>
        ) : null}
        {precontractSlots !== null ? <PrecontractInfoShell slots={precontractSlots} /> : null}
        <PayObligationCtaShell
          confirmed={payObligationConfirmed}
          onConfirmedChange={setPayObligationConfirmed}
          amountMinor={
            !isDonationSession && checkoutSession != null
              ? checkoutSession.amountMinor
              : undefined
          }
          currency={
            !isDonationSession && checkoutSession != null
              ? checkoutSession.currency
              : undefined
          }
          showVatHonesty={!isDonationSession && checkoutSession != null}
          apiTenantCode={!isDonationSession ? tenantCode : null}
          showPolicyLanguageToggle={!isDonationSession}
        />
        {paymentRailUiStep === 'gateway-methods' ? (
          <GatewayMethodsStep
            methods={gatewayMethodOptions}
            gatewayProviderSlotId={resolveGatewayProviderSlotId(gatewayMethodsResponse)}
            loading={gatewayMethodsLoading}
            startingMethodId={gatewayStartingMethodId}
            disabled={payActionsDisabled || !payObligationConfirmed || ageGateBlocksPay}
            onSelectMethod={selectGatewayMethodIfAgeGateAllows}
          />
        ) : (
          <>{renderPrimaryPaymentMethodSection()}</>
        )}
        {paymentError !== null ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {toCustomerFacingErrorMessage(paymentError, {
              fallback: t('checkout.paymentStartFailed'),
            })}
          </p>
        ) : null}
        {gatewayHint !== null ? (
          <p className="text-xs text-[var(--color-on-surface-muted)]">{gatewayHint}</p>
        ) : null}
      </div>
      {!isDonationSession ? (
        <AgeGateDialog
          tenantCode={tenantCode}
          products={shopProductsQuery.data ?? []}
          reviewRisk={sellerLegalQuery.data?.reviewRisk}
          inputsResolved={ageGateInputsResolved}
          testIdPrefix="customer-age-gate-checkout"
        />
      ) : null}
    </Card>
  );
}
